import React from "react";

import { useFrame } from "../../shared/frame";

import styles from "./index.module.scss";

/** The attribute Integration puts on the logo ticker, which the dome keys off */
export const COVER_ANCHOR_ATTRIBUTE = "data-expertise-cover-anchor";

/** Scale at the start of the growth */
const COVER_FROM = 0.26;
/** Scroll distance the desktop growth takes, in px */
const COVER_PX = 1600;
/** Fraction of the viewport the ticker's top must reach to start it */
const COVER_AT = 1.2;
/** Without a ticker: how far below the fold the content top still is */
const COVER_LEAD = 0.12;
/** The dome's top offset, in design units */
const COVER_TOP = -400;
/** Radius at scale 1, in design units: desktop, and 1023 and below */
const COVER_R = 1432.5;
const COVER_R_SM = 2292;
/** Stacked: least separation between the two anchors' scales */
const COVER_EDGE_SM = 0.02;
/** Stacked: px the arc keeps clear of the header and the ticker */
const COVER_GAP_SM = 56;
const STACKED_MAX = 1023;

const sag = (d: number, rho: number) =>
    rho > d ? rho - Math.sqrt(rho * rho - d * d) : rho;

/** The scale that puts the dome's arc where the prototype has it */
const coverScale = (
    section: HTMLElement,
    cover: HTMLElement,
    header: Element | null,
) => {
    const vh = window.innerHeight;
    const width = window.innerWidth;
    const u = width / 1440;
    // The section's content top: its box starts higher by the dome's headroom
    const rTop =
        section.getBoundingClientRect().top +
        (parseFloat(getComputedStyle(section).paddingTop) || 0);
    const R = width <= STACKED_MAX ? COVER_R_SM : COVER_R;
    // The apex at scale 0 below the content top, and how far it rises per
    // unit of scale
    const A = (COVER_TOP + R) * u;
    const B = R * u;
    const ticker = document.querySelector(`[${COVER_ANCHOR_ATTRIBUTE}]`);

    if (width > STACKED_MAX) {
        const from = ticker
            ? vh * COVER_AT - ticker.getBoundingClientRect().top
            : vh * (1 + COVER_LEAD) - rTop;
        const p = Math.max(0, Math.min(1, from / COVER_PX));
        const e = p * p * (3 - 2 * p);
        return COVER_FROM + (1 - COVER_FROM) * e;
    }

    // Stacked: the scale is solved from the geometry, a straight line between
    // the arc clearing the header as it arrives at the bottom of the screen
    // and the arc clearing the ticker as it leaves the top
    const padTop = header ? header.getBoundingClientRect().top - rTop : 248;
    const tickBot = ticker ? rTop - ticker.getBoundingClientRect().bottom : 54;
    const box = cover.getBoundingClientRect();
    const centre = (box.left + box.right) / 2;
    const reach = (el: Element | null) => {
        if (!el) return 0;
        const b = el.getBoundingClientRect();
        return Math.max(Math.abs(b.left - centre), Math.abs(b.right - centre));
    };
    const s1 = Math.min(
        (A - padTop + COVER_GAP_SM + sag(reach(header), A - padTop)) / B,
        1,
    );
    const s2 = Math.max(
        (A + tickBot - COVER_GAP_SM + sag(reach(ticker), A + tickBot)) / B,
        s1 + COVER_EDGE_SM,
    );
    const t1 = vh - padTop;
    const t2 = tickBot;
    const s = s1 + ((rTop - t1) * (s2 - s1)) / (t2 - t1);
    return Math.max(COVER_FROM, Math.min(1, s));
};

interface Props {
    sectionRef: React.RefObject<HTMLElement | null>;
    /** The section header, which the stacked arc must clear */
    headerSelector: string;
}

/**
 * The page-coloured dome that swells up over the Work gradient. Its centre is
 * pinned and only its scale follows the scroll, so the curved edge sweeps up
 * the screen.
 */
export const Cover = (props: Props) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [near, setNear] = React.useState(false);

    React.useEffect(() => {
        const section = props.sectionRef.current;
        if (!section) return;
        const observer = new IntersectionObserver(
            (entries) => setNear(entries[entries.length - 1].isIntersecting),
            { rootMargin: "50% 0px" },
        );
        observer.observe(section);
        return () => observer.disconnect();
    }, [props.sectionRef]);

    useFrame(() => {
        const section = props.sectionRef.current;
        const cover = ref.current;
        if (!section || !cover) return;
        const s = coverScale(
            section,
            cover,
            section.querySelector(props.headerSelector),
        );
        cover.style.setProperty("--cover-s", s.toFixed(4));
    }, near);

    return <div ref={ref} className={styles.cover} aria-hidden="true" />;
};
